import { type ComponentProps } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { twMerge } from 'tailwind-merge'
import { useSidepanel } from '@renderer/overlay/atoms'
import { useSidepanelSide } from '../../../atoms'
import { useSidepanelSurfaceBackground } from '../../../hooks/use-sidepanel-surface-background'
import { oppositeSide, sidePaddingProp } from '@shared/sidepanel/side'

type SidepanelContentProps = ComponentProps<'div'>

export function SidepanelContent({ children, className, ...props }: SidepanelContentProps) {
  const [sidepanelState] = useSidepanel()
  const [applyBlur] = useGlobalStore('settings', 'app.applyBlur', false)
  const side = useSidepanelSide()
  const surfaceBackground = useSidepanelSurfaceBackground()

  return (
    <div
      className={twMerge(
        'pointer-events-none relative flex h-full flex-col p-3',
        !applyBlur && sidepanelState === 'peeking' && 'cursor-pointer',
        className,
      )}
      style={{
        // Slim gutter on the inner edge (away from the screen edge)
        [sidePaddingProp(oppositeSide(side))]: 'var(--sidepanel-left-padding, 12px)',
      }}
      {...props}
    >
      {/* The unified panel surface: one rounded card that hosts the top bar,
          the section stack, and the bottom chrome. Header/footer absolutes
          resolve against this box, and its overflow clip is what cuts scrolled
          content at the panel silhouette. No border: the shadow's 1px
          edge is the outline, flipping to white on dark.

          The shadow sits on the wrapper, not the surface: a blurred shadow
          re-rasterises with every hover repaint of the element that carries
          it, which measured ~2.5x the cost of the repaint itself while
          sweeping the pointer over rows. The surface is promoted to its own
          layer so those repaints stay panel-sized and never touch the
          wrapper or the full-screen document layer. */}
      <div className="corner-smooth relative h-full w-full rounded-2xl shadow-lg">
        <div
          className={twMerge(
            'corner-smooth pointer-events-auto relative flex h-full w-full flex-col overflow-hidden rounded-2xl transition-[background-color] duration-200 ease-out will-change-transform',
            surfaceBackground,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
